import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Play, CheckCircle, Target, Zap, FileText, Wrench, Lock, Loader2, BookOpen, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LightBackground from '@/components/LightBackground';
import { getAcademyById } from '@/data/academyData';
import { useModuleProgress } from '@/hooks/useModuleProgress';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { useFreemiumAccess } from '@/hooks/useFreemiumAccess';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import LockedContentModal from '@/components/LockedContentModal';
import PlanSelectionDialog from '@/components/PlanSelectionDialog';
import { LanguageSelector } from '@/components/LanguageSelector';
import { TranslatedText } from '@/components/TranslatedText';

interface ClassData {
  id: string;
  class_number: number;
  title: string;
}

interface ClassCompletion {
  class_id: string;
  passed: boolean;
}

interface ModuleData {
  id: string;
  module_number: number;
  title: string;
  description: string | null;
  full_description: string | null;
  transformation: string | null;
  skills: string[] | null;
  tech: string[] | null;
}

interface DeliverableData {
  id: string;
  title: string;
  sort_order: number;
}

const Module = () => {
  const { academyId, moduleId } = useParams<{ academyId: string; moduleId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const academy = getAcademyById(academyId || '');
  const staticModule = academy?.modules.find((m) => m.id === parseInt(moduleId || '0'));
  const { isModuleCompleted, toggleModuleCompletion, isLoading } = useModuleProgress();
  const { isModuleUnlocked, isLoading: accessLoading } = useModuleAccess();
  const { hasFullAccess, isContentFree, canAccessModule: checkCanAccessModule, clientType, loading: freemiumLoading, isMonthlyPlan, getNextRequiredModule } = useFreemiumAccess();
  const [isToggling, setIsToggling] = useState(false);
  const [showLockedModal, setShowLockedModal] = useState(false);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [lockReason, setLockReason] = useState<'freemium' | 'monthly_incomplete'>('freemium');
  
  // Database module data state
  const [dbModule, setDbModule] = useState<ModuleData | null>(null);
  const [deliverables, setDeliverables] = useState<DeliverableData[]>([]);
  
  // Database classes state
  const [dbModuleId, setDbModuleId] = useState<string | null>(null);
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [classCompletions, setClassCompletions] = useState<ClassCompletion[]>([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [clientId, setClientId] = useState<string | null>(null);


  // Fetch client ID
  useEffect(() => {
    const fetchClientId = async () => {
      if (!user) return;
      
      const { data } = await supabase
        .from('clients')
        .select('id')
        .eq('user_id', user.id)
        .single();
      
      if (data) {
        setClientId(data.id);
      }
    };
    
    fetchClientId();
  }, [user]);

  // Fetch the database module ID, metadata, and classes
  useEffect(() => {
    const fetchModuleData = async () => {
      if (!academyId || !moduleId) return;
      
      setLoadingClasses(true);
      
      try {
        // First, get the database module with full metadata
        const { data: moduleData, error: moduleError } = await supabase
          .from('modules')
          .select('id, module_number, title, description, full_description, transformation, skills, tech')
          .eq('academy_id', academyId)
          .eq('module_number', parseInt(moduleId))
          .single();
        
        if (moduleError || !moduleData) {
          console.log('No database module found, using static data');
          setLoadingClasses(false);
          return;
        }
        
        setDbModuleId(moduleData.id);
        setDbModule(moduleData);
        
        // Fetch deliverables for this module
        const { data: deliverablesData } = await supabase
          .from('module_deliverables')
          .select('id, title, sort_order')
          .eq('module_id', moduleData.id)
          .order('sort_order');
        
        setDeliverables(deliverablesData || []);
        
        // Fetch classes for this module
        const { data: classesData, error: classesError } = await supabase
          .from('classes')
          .select('id, class_number, title')
          .eq('module_id', moduleData.id)
          .order('class_number');
        
        if (classesError) throw classesError;
        
        setClasses(classesData || []);
        
        // Fetch completions if we have a client ID
        if (clientId && classesData && classesData.length > 0) {
          const classIds = classesData.map(c => c.id);
          const { data: completionsData } = await supabase
            .from('class_completions')
            .select('class_id, passed')
            .eq('client_id', clientId)
            .in('class_id', classIds);
          
          setClassCompletions(completionsData || []);
        }
      } catch (error) {
        console.error('Error fetching module data:', error);
      } finally {
        setLoadingClasses(false);
      }
    };
    
    fetchModuleData();
  }, [academyId, moduleId, clientId]);

  if (!academy || !staticModule) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-foreground">Module not found</p>
      </div>
    );
  }

  if (accessLoading || isLoading || freemiumLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const isUnlocked = isModuleUnlocked(academy.id, staticModule.id);
  
  // Check if this module is the free teaser (Module 1 of first academy)
  const isFreeModule = isContentFree(academyId || '', parseInt(moduleId || '0'));
  
  // Use the centralized access check that handles all client types
  const canAccessModule = checkCanAccessModule(academyId || '', parseInt(moduleId || '0'));

  // Use database module data if available, otherwise fall back to static
  const moduleTitle = dbModule?.title || staticModule.title;
  const moduleNumber = dbModule?.module_number || staticModule.id;

  // Calculate the required previous module for monthly users
  const previousModuleNumber = isMonthlyPlan ? parseInt(moduleId || '0') - 1 : undefined;

  // If module is locked (and not free), show locked state
  if (!canAccessModule) {
    // Determine lock reason
    const currentLockReason = isMonthlyPlan ? 'monthly_incomplete' : 'freemium';
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-background text-foreground overflow-hidden">
        <LightBackground variant="subtle" />
        <LockedContentModal 
          isOpen={showLockedModal} 
          onClose={() => setShowLockedModal(false)} 
          contentType="module"
          lockReason={lockReason}
          previousModuleNumber={previousModuleNumber}
        />
        
        <header className="relative z-50 px-6 py-4 border-b border-border/20">
          <div className="max-w-7xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => navigate(`/academy/${academyId}`)}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <TranslatedText>Back to Modules</TranslatedText>
            </Button>
          </div>
        </header>

        <main className="relative z-10 px-6 py-24">
          <div className="max-w-md mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-muted/20 flex items-center justify-center">
              <Lock className="w-10 h-10 text-muted-foreground" />
            </div>
            <h1 className="text-2xl font-bold mb-4 text-foreground">
              <TranslatedText>
                {isMonthlyPlan ? 'Complete Previous Module' : 'Module Locked'}
              </TranslatedText>
            </h1>
            <p className="text-muted-foreground mb-6">
              <TranslatedText>
                {isMonthlyPlan 
                  ? `Complete 100% of Module ${previousModuleNumber} to unlock this module. Finish all classes and pass the exercises!`
                  : 'Subscribe now to unlock full access to all modules and classes!'
                }
              </TranslatedText>
            </p>
            <div className="space-y-3">
              {isMonthlyPlan ? (
                <Button
                  onClick={() => navigate(`/academy/${academyId}`)}
                  variant="cta"
                  className="w-full"
                >
                  <TranslatedText>Back to Academy</TranslatedText>
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => {
                      setLockReason('freemium');
                      setShowLockedModal(true);
                    }}
                    variant="cta"
                    className="w-full"
                  >
                    <TranslatedText>Subscribe Now</TranslatedText>
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/academy/${academyId}`)}
                    className="w-full"
                  >
                    <TranslatedText>Back to Academy</TranslatedText>
                  </Button>
                </>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  const isCompleted = isModuleCompleted(academy.id, staticModule.id);

  const handleToggleComplete = async () => {
    setIsToggling(true);
    await toggleModuleCompletion(academy.id, staticModule.id);
    setIsToggling(false);
  };

  const isClassCompleted = (classId: string) => {
    return classCompletions.some(c => c.class_id === classId && c.passed);
  };

  const handleClassClick = (classNumber: number) => {
    // Use dbModuleId (UUID) for class navigation since Class.tsx queries by module_id UUID
    navigate(`/academy/${academyId}/module/${moduleId}/class/${classNumber}`, {
      state: { dbModuleId }
    });
  };

  // Generate placeholder classes if none in database
  const displayClasses = classes.length > 0 ? classes : Array.from({ length: 10 }, (_, i) => ({
    id: `placeholder-${i + 1}`,
    class_number: i + 1,
    title: `Class ${i + 1}`,
  }));

  const completedCount = classCompletions.filter(c => c.passed).length;
  const canMarkComplete = completedCount >= 9;

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-background text-foreground overflow-hidden">
      <LightBackground variant="subtle" />

      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/3 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-50 px-6 py-4 border-b border-border/20">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(`/academy/${academyId}`)}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            <TranslatedText>Back to Modules</TranslatedText>
          </Button>
          
          <div className="flex items-center gap-2">
            <LanguageSelector variant="ghost" className="text-muted-foreground hover:text-foreground" />
            <Button
              variant={isCompleted ? "outline" : "default"}
              onClick={handleToggleComplete}
              disabled={isToggling || isLoading || !canMarkComplete}
              className={`${isCompleted ? "border-primary text-primary hover:bg-primary/10" : ""} ${!canMarkComplete && !isCompleted ? "opacity-40 cursor-not-allowed shadow-none" : ""}`}
              title={!canMarkComplete && !isCompleted ? `Complete at least 9 classes first (${completedCount}/9)` : undefined}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {isToggling ? (
                <TranslatedText>Updating...</TranslatedText>
              ) : isCompleted ? (
                <TranslatedText>Completed</TranslatedText>
              ) : !canMarkComplete ? (
                <TranslatedText>{`${completedCount}/9 Classes`}</TranslatedText>
              ) : (
                <TranslatedText>Mark as Complete</TranslatedText>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Module Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-primary text-sm font-medium uppercase tracking-wider">
                <TranslatedText>Module</TranslatedText> {moduleNumber}
              </span>
              {isCompleted && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-primary/20 text-primary">
                  <TranslatedText>Completed</TranslatedText>
                </span>
              )}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mt-2 mb-4 bg-gradient-to-r from-foreground to-primary bg-clip-text text-transparent">
              <TranslatedText cacheKey={dbModule ? `module_title_${dbModule.id}` : `module_title_${academyId}_${moduleId}`}>
                {moduleTitle}
              </TranslatedText>
            </h1>
            <p className="text-muted-foreground">
              {displayClasses.length} <TranslatedText>classes</TranslatedText> • {academy.name}
              {classes.length > 0 && completedCount > 0 && (
                <span className="ml-2 text-primary">• {completedCount}/{classes.length} <TranslatedText>completed</TranslatedText></span>
              )}
            </p>
          </motion.div>

          {/* Module Details - Only show if rich data available from database */}
          {dbModule && (dbModule.transformation || dbModule.full_description || dbModule.skills?.length || deliverables.length || dbModule.tech?.length) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mb-12 space-y-6"
            >
              {/* Transformation */}
              {dbModule.transformation && (
                <div className="p-6 rounded-xl glass-panel-strong border-primary/20">
                  <div className="flex items-center gap-3 mb-3">
                    <Target className="w-5 h-5 text-primary" />
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-primary">
                      <TranslatedText>Transformation Goal</TranslatedText>
                    </h3>
                  </div>
                  <p className="text-foreground/90">
                    <TranslatedText cacheKey={`module_transformation_${dbModule.id}`}>
                      {dbModule.transformation}
                    </TranslatedText>
                  </p>
                </div>
              )}

              {/* Full Description */}
              {dbModule.full_description && (
                <div className="p-6 rounded-xl glass-panel">
                  <p className="text-muted-foreground leading-relaxed">
                    <TranslatedText cacheKey={`module_full_desc_${dbModule.id}`}>
                      {dbModule.full_description}
                    </TranslatedText>
                  </p>
                </div>
              )}

              {/* Skills, Deliverables, Tech Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Skills */}
                {dbModule.skills && dbModule.skills.length > 0 && (
                  <div className="p-5 rounded-xl glass-panel">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-4 h-4 text-primary" />
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <TranslatedText>Skills</TranslatedText>
                      </h4>
                    </div>
                    <ul className="space-y-2">
                      {dbModule.skills.map((skill, idx) => (
                        <li key={idx} className="text-sm text-foreground/80 flex items-start gap-2">
                          <span className="text-primary mt-1">•</span>
                          <TranslatedText cacheKey={`module_skill_${dbModule.id}_${idx}`}>
                            {skill}
                          </TranslatedText>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Deliverables */}
                {deliverables.length > 0 && (
                  <div className="p-5 rounded-xl glass-panel">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-4 h-4 text-primary" />
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <TranslatedText>Deliverables</TranslatedText>
                      </h4>
                    </div>
                    <ul className="space-y-2">
                      {deliverables.map((item, idx) => (
                        <li key={idx} className="text-sm text-foreground/80 flex items-start gap-2">
                          <span className="text-primary mt-1">•</span>
                          <TranslatedText cacheKey={`deliverable_${item.id}`}>
                            {item.title}
                          </TranslatedText>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Tech */}
                {dbModule.tech && dbModule.tech.length > 0 && (
                  <div className="p-5 rounded-xl glass-panel">
                    <div className="flex items-center gap-2 mb-3">
                      <Wrench className="w-4 h-4 text-primary" />
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <TranslatedText>Tools</TranslatedText>
                      </h4>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {dbModule.tech.map((tool, idx) => (
                        <span
                          key={idx}
                          className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/20"
                        >
                          <TranslatedText cacheKey={`module_tool_${dbModule.id}_${idx}`}>
                            {tool}
                          </TranslatedText>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Upgrade Banner for Freemium Users */}
          {!hasFullAccess && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="mb-8 p-5 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-200 flex flex-col sm:flex-row items-center justify-between gap-4"
            >
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-blue-600 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    <TranslatedText>Only Class 1 is free in this module</TranslatedText>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <TranslatedText>Subscribe to unlock all 10 classes + exercises</TranslatedText>
                  </p>
                </div>
              </div>
              <Button
                onClick={() => setShowPlanDialog(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl shrink-0"
                size="sm"
              >
                <TranslatedText>Upgrade Now</TranslatedText>
              </Button>
            </motion.div>
          )}

          {/* Classes List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-lg font-semibold mb-4 text-foreground flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Classes
            </h2>
            
            {loadingClasses ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-3">
                {displayClasses.map((classItem, index) => {
                  const isClassDone = isClassCompleted(classItem.id);
                  const isPlaceholder = classItem.id.startsWith('placeholder');
                  const hasContent = classes.length > 0 && !isPlaceholder;
                  
                  return (
                    <motion.div
                      key={classItem.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.4, delay: 0.3 + index * 0.05 }}
                      onClick={() => hasContent && handleClassClick(classItem.class_number)}
                      className={`relative p-5 rounded-xl transition-all duration-300 glass-panel ${
                        hasContent 
                          ? 'cursor-pointer hover:border-primary/50' 
                          : 'opacity-60 cursor-not-allowed'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isClassDone 
                              ? 'bg-primary/20 text-primary' 
                              : 'bg-muted/20 text-muted-foreground'
                          }`}>
                            {isClassDone ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : (
                              <Play className="w-4 h-4" />
                            )}
                          </div>
                          <div>
                            <span className="text-xs text-muted-foreground">
                              <TranslatedText>Class</TranslatedText> {classItem.class_number}
                            </span>
                            <h3 className="text-base font-medium text-foreground">
                              <TranslatedText cacheKey={`class_title_${classItem.id}`}>{classItem.title}</TranslatedText>
                            </h3>
                          </div>
                        </div>

                        {hasContent ? (
                          <span className={`text-xs px-3 py-1 rounded-full ${
                            isClassDone 
                              ? 'bg-primary/20 text-primary' 
                              : 'bg-muted/20 text-muted-foreground'
                          }`}>
                            {isClassDone ? 'Completed' : 'Start'}
                          </span>
                        ) : (
                          <span className="text-xs px-3 py-1 rounded-full bg-muted/10 text-muted-foreground">
                            Coming Soon
                          </span>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </motion.div>

          {/* Bottom Upgrade CTA for Freemium */}
          {!hasFullAccess && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 text-white text-center"
            >
              <Sparkles className="w-6 h-6 mx-auto mb-2 text-white/80" />
              <h3 className="text-lg font-bold mb-1">
                <TranslatedText>Ready to unlock everything?</TranslatedText>
              </h3>
              <p className="text-sm text-white/80 mb-4">
                <TranslatedText>Get access to all 500 classes, exercises, and certificates</TranslatedText>
              </p>
              <Button
                onClick={() => setShowPlanDialog(true)}
                className="bg-background text-primary hover:bg-background/90 font-semibold rounded-xl"
                size="lg"
              >
                <TranslatedText>See Plans & Pricing</TranslatedText>
              </Button>
            </motion.div>
          )}

          {/* Complete Module Button at Bottom */}
          {hasFullAccess && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="mt-12 text-center"
            >
              <Button
                size="lg"
                variant={isCompleted ? "outline" : "default"}
                onClick={handleToggleComplete}
                disabled={isToggling || isLoading || !canMarkComplete}
                className={`px-8 ${isCompleted ? "border-primary text-primary hover:bg-primary/10" : ""} ${!canMarkComplete && !isCompleted ? "opacity-40 cursor-not-allowed shadow-none" : ""}`}
                title={!canMarkComplete && !isCompleted ? `Complete at least 9 classes first (${completedCount}/9)` : undefined}
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                {isToggling ? 'Updating...' : isCompleted ? 'Module Completed ✓' : !canMarkComplete ? `Complete ${9 - completedCount} More Classes` : 'Mark Module as Complete'}
              </Button>
            </motion.div>
          )}
        </div>
      </main>
      <PlanSelectionDialog open={showPlanDialog} onOpenChange={setShowPlanDialog} />
    </div>
  );
};

export default Module;